/**
 * Service Line Filter Component
 * 
 * UI component for filtering opportunities by service line
 * Generic component that accepts service lines as a prop
 */

import React from 'react';
import { Card, Button, Badge } from '@tracekit/core';
import { Folder, X } from 'lucide-react';

export interface ServiceLine {
  id: string;
  name: string;
  description: string;
  opportunityCount?: number;
  color?: string;
}

export interface ServiceLineFilterProps {
  serviceLines: ServiceLine[];
  selectedServiceLines: string[];
  onChange: (serviceLines: string[]) => void;
  onClear: () => void;
}

export function ServiceLineFilter({ 
  serviceLines,
  selectedServiceLines, 
  onChange, 
  onClear 
}: ServiceLineFilterProps) {
  const handleServiceLineToggle = (serviceLineId: string) => {
    const newSelection = selectedServiceLines.includes(serviceLineId)
      ? selectedServiceLines.filter(id => id !== serviceLineId)
      : [...selectedServiceLines, serviceLineId];
    onChange(newSelection);
  };
  
  const handleSelectAll = () => {
    onChange(serviceLines.map(sl => sl.id));
  };
  
  const hasActiveFilters = selectedServiceLines.length > 0;
  
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Folder className="h-5 w-5 text-gray-600" />
          <h3 className="text-lg font-semibold">Service Line Filter</h3>
          {hasActiveFilters && (
            <Badge variant="primary" className="ml-2">
              {selectedServiceLines.length} selected
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {hasActiveFilters && (
            <>
              <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={onClear}>
                <X className="h-4 w-4 mr-1" />
                Clear
              </Button>
            </>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {serviceLines.map(serviceLine => {
          const isSelected = selectedServiceLines.includes(serviceLine.id);
          
          return (
            <button
              key={serviceLine.id}
              onClick={() => handleServiceLineToggle(serviceLine.id)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                isSelected
                  ? 'border-blue-500 bg-blue-50 shadow-sm'
                  : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50'
              }`}
            >
              <div className="flex items-start justify-between mb-1">
                <h4 className={`font-semibold text-sm ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
                  {serviceLine.name}
                </h4>
                {isSelected && (
                  <div className="w-5 h-5 rounded-full bg-blue-500 flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-600 mb-2">{serviceLine.description}</p>
              {serviceLine.opportunityCount !== undefined && (
                <Badge variant="default" className="text-xs">
                  {serviceLine.opportunityCount}+ opportunities
                </Badge>
              )}
            </button>
          );
        })}
      </div>
      
      {!hasActiveFilters && (
        <p className="text-sm text-gray-500 mt-4 text-center">
          Select one or more service lines to filter opportunities
        </p>
      )}
    </Card>
  );
}

